/**
 * Copyright (c) 2026 Farelanes LLC. All rights reserved.
 *
 * Path constants, protected-route list, and rewrite of legacy /dashboard/* bookmarks.
 */

/**
 * Route Constants
 * Centralized route definitions for the application
 */

export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',

  DASHBOARD: '/dashboard',
  DASHBOARD_HOME: '/dashboard',
  DASHBOARD_SEARCH: '/search',
  DASHBOARD_SEARCH_EXPLORE: '/search/explore',
  DASHBOARD_SEARCH_ADVANCED: '/search/advanced',
  DASHBOARD_SEARCH_ANALYTICS: '/search/analytics',
  DASHBOARD_FAVORITES: '/favorites',
  DASHBOARD_INSIGHTS: '/insights',
  DASHBOARD_PROFILE: '/setup',
  DASHBOARD_PROFILE_COMPANY: '/setup/company',
  DASHBOARD_PROFILE_TICKERS: '/setup',
  DASHBOARD_ADMIN: '/admin',
  DASHBOARD_RFQ: '/rfq',
  DASHBOARD_MILEMAKER: '/milemaker',
  DASHBOARD_NEWS: '/news',
  DASHBOARD_TOP_RATES: '/top-rates',

  // Legacy deep-link: no/Default tier users are logged out to farelanes.com/shop.
  NO_SUBSCRIPTION: '/no-subscription',

  // Auth routes
  CALLBACK: '/callback',

  // Fallback
  NOT_FOUND: '*'
};

export const DASHBOARD_SHELL_PATHS = [
  ROUTES.DASHBOARD,
  `${ROUTES.DASHBOARD_SEARCH}/*`,
  ROUTES.DASHBOARD_FAVORITES,
  ROUTES.DASHBOARD_INSIGHTS,
  `${ROUTES.DASHBOARD_PROFILE}/*`,
  ROUTES.DASHBOARD_ADMIN,
  ROUTES.DASHBOARD_RFQ,
  ROUTES.DASHBOARD_MILEMAKER,
  ROUTES.DASHBOARD_NEWS,
  ROUTES.DASHBOARD_TOP_RATES,
];

const LEGACY_DASHBOARD_PREFIX_REDIRECTS = {
  milemaker: ROUTES.DASHBOARD_MILEMAKER,
  search: ROUTES.DASHBOARD_SEARCH,
  insights: ROUTES.DASHBOARD_INSIGHTS,
  rfq: ROUTES.DASHBOARD_RFQ,
  profile: ROUTES.DASHBOARD_PROFILE,
  setup: ROUTES.DASHBOARD_PROFILE,
  admin: ROUTES.DASHBOARD_ADMIN,
  news: ROUTES.DASHBOARD_NEWS,
  'top-rates': ROUTES.DASHBOARD_TOP_RATES,
  favorites: ROUTES.DASHBOARD_FAVORITES,
};

export function rewriteLegacyDashboardPath(pathname) {
  const suffix = pathname.replace(/^\/dashboard\/?/, '');
  if (!suffix) {
    return ROUTES.DASHBOARD;
  }
  const [head, ...rest] = suffix.split('/');
  const mapped = LEGACY_DASHBOARD_PREFIX_REDIRECTS[head];
  if (!mapped) {
    return ROUTES.DASHBOARD;
  }
  return rest.length ? `${mapped}/${rest.join('/')}` : mapped;
}

/**
 * List of routes that require authentication
 */
export const PROTECTED_ROUTES = [
  ROUTES.DASHBOARD,
  ROUTES.DASHBOARD_SEARCH,
  ROUTES.DASHBOARD_SEARCH_EXPLORE,
  ROUTES.DASHBOARD_SEARCH_ADVANCED,
  ROUTES.DASHBOARD_SEARCH_ANALYTICS,
  ROUTES.DASHBOARD_FAVORITES,
  ROUTES.DASHBOARD_INSIGHTS,
  ROUTES.DASHBOARD_PROFILE,
  ROUTES.DASHBOARD_PROFILE_COMPANY,
  ROUTES.DASHBOARD_PROFILE_TICKERS,
  ROUTES.DASHBOARD_ADMIN,
  ROUTES.DASHBOARD_RFQ,
  ROUTES.DASHBOARD_MILEMAKER,
  ROUTES.DASHBOARD_NEWS,
  ROUTES.DASHBOARD_TOP_RATES,
  ROUTES.NO_SUBSCRIPTION,
];

/**
 * Helper to check if a route is protected
 */
export const isProtectedRoute = (path) => {
  return PROTECTED_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
};

/**
 * Legacy URL query param mappings (for backward compatibility during migration)
 * Maps old query params to new routes
 */
export const LEGACY_TAB_MAPPINGS = {
  // Legacy Setup links now route to Tickers & City Pairs
  'tab=setup&subtab=profile': ROUTES.DASHBOARD_PROFILE_TICKERS,
  'tab=setup&subtab=company': ROUTES.DASHBOARD_PROFILE_TICKERS,
  'tab=setup&subtab=subscriptions': ROUTES.DASHBOARD_PROFILE_TICKERS,
  'tab=setup&subtab=tickers': ROUTES.DASHBOARD_PROFILE_TICKERS,
};
